/**
 * `ExperienceRangeSelect` — selettore controllato della **fascia di anni di
 * esperienza** (`EXPERIENCE_RANGE_VALUES`).
 *
 * Affianca `LivelloSelect` e `StarRating` nell'autovalutazione delle proprie
 * skill. A differenza del Livello, gli anni di esperienza sono un dato
 * **puramente informativo e opzionale**: il valore "non dichiarato" (`null`) è
 * uno stato finale valido e sempre riselezionabile tramite l'opzione
 * placeholder. Il componente non salva né valida: si limita a notificare la
 * scelta al genitore.
 *
 * La scala (`EXPERIENCE_RANGE_VALUES`, tipo `ExperienceRange`,
 * `EXPERIENCE_RANGE_LABELS`) è definita in `shared/dto` e importata da lì: il
 * componente NON ridefinisce la scala né duplica le etichette.
 */
import { useId } from 'react';
import {
  EXPERIENCE_RANGE_LABELS,
  EXPERIENCE_RANGE_VALUES,
  isExperienceRange,
  type ExperienceRange,
} from '../shared/dto';

export interface ExperienceRangeSelectProps {
  /** Fascia corrente, oppure `null` se non dichiarata. */
  value: ExperienceRange | null;
  /**
   * Notifica la fascia scelta dall'utente: un valore valido oppure `null`
   * quando l'utente riseleziona il placeholder (dato opzionale).
   */
  onChange: (value: ExperienceRange | null) => void;
  /** Etichetta accessibile del controllo (es. "Anni di esperienza per React"). */
  label: string;
  /** Disabilita l'interazione (sola lettura / non modificabile). */
  disabled?: boolean;
  /** Testo dell'opzione placeholder mostrata quando `value` è `null`. */
  placeholder?: string;
  /** `id` opzionale per collegare una `<label>` esterna. */
  id?: string;
  /** Classe extra sul `<select>`. */
  className?: string;
}

/**
 * Select opzionale della fascia di anni di esperienza. Il placeholder è sempre
 * selezionabile e rappresenta lo stato "non dichiarato" (`null`).
 */
export function ExperienceRangeSelect({
  value,
  onChange,
  label,
  disabled = false,
  placeholder = 'Anni di esperienza…',
  id,
  className,
}: ExperienceRangeSelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value;
    if (isExperienceRange(next)) {
      onChange(next);
      return;
    }
    // Placeholder (valore vuoto): il campo è opzionale, quindi azzerabile.
    onChange(null);
  };

  return (
    <select
      id={selectId}
      className={
        className ? `experience-range-select ${className}` : 'experience-range-select'
      }
      aria-label={label}
      value={value ?? ''}
      disabled={disabled}
      data-empty={value === null || undefined}
      onChange={handleChange}
    >
      {/* Opzione placeholder: stato "non dichiarato" (`null`), sempre
          selezionabile perché il campo è opzionale. */}
      <option value="">{placeholder}</option>
      {EXPERIENCE_RANGE_VALUES.map((range) => (
        <option key={range} value={range}>
          {EXPERIENCE_RANGE_LABELS[range]}
        </option>
      ))}
    </select>
  );
}

export default ExperienceRangeSelect;
